var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { EMessageChannel, EMessagePurpose, } from "@/types/messaging";
import { enabledChannels } from "@/services/messaging";
import { MESSAGE_TEMPLATE_METHODS } from "@/types/templates";
import { templateOffersMethod } from "@/services/templateMethods";
/**
 * An instant message as it is sent: the provider's template and language only
 * when set, so that without them the event's binding is used.
 */
var instantMessage = function (config) {
    var _a, _b;
    if (!config) {
        return undefined;
    }
    var providerTemplate = (_a = config.provider_template) === null || _a === void 0 ? void 0 : _a.trim();
    var providerLanguage = (_b = config.provider_language) === null || _b === void 0 ? void 0 : _b.trim();
    return __assign(__assign({ message: config.message, parameters: config.parameters }, (providerTemplate ? { provider_template: providerTemplate } : {})), (providerLanguage ? { provider_language: providerLanguage } : {}));
};
var sendContent = function (content) { return (__assign(__assign({}, content), { whatsapp: instantMessage(content.whatsapp), viber: instantMessage(content.viber), messenger: instantMessage(content.messenger) })); };
export var buildSendTemplatePayload = function (state) {
    return __assign(__assign(__assign({ audience_selection: state.audienceSelection, audience_voter_ids: state.voterIds, schedule_now: state.scheduleNow, schedule_date: state.scheduleDate, schedule_local: state.scheduleLocal, schedule_timezone: state.scheduleTimezone }, (state.alias ? { alias: state.alias } : {})), sendContent(state.content)), { secret_attribute_names: state.secretAttributeNames });
};
/** Templates that send a message on at least one channel. */
export var sendableTemplates = function (rows) {
    return rows.filter(function (row) {
        return MESSAGE_TEMPLATE_METHODS.some(function (candidate) { return templateOffersMethod(row, candidate); });
    });
};
var hasContent = function (content, channel) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
    switch (channel) {
        case EMessageChannel.EMAIL:
            return !!((_b = (_a = content.email) === null || _a === void 0 ? void 0 : _a.subject) === null || _b === void 0 ? void 0 : _b.trim()) || !!((_d = (_c = content.email) === null || _c === void 0 ? void 0 : _c.plaintext_body) === null || _d === void 0 ? void 0 : _d.trim());
        case EMessageChannel.SMS:
            return !!((_f = (_e = content.sms) === null || _e === void 0 ? void 0 : _e.message) === null || _f === void 0 ? void 0 : _f.trim());
        case EMessageChannel.WHATSAPP:
            return !!((_h = (_g = content.whatsapp) === null || _g === void 0 ? void 0 : _g.message) === null || _h === void 0 ? void 0 : _h.trim());
        case EMessageChannel.VIBER:
            return !!((_k = (_j = content.viber) === null || _j === void 0 ? void 0 : _j.message) === null || _k === void 0 ? void 0 : _k.trim());
        case EMessageChannel.MESSENGER:
            return !!((_m = (_l = content.messenger) === null || _l === void 0 ? void 0 : _l.message) === null || _m === void 0 ? void 0 : _m.trim());
    }
};
/** Channels among `channels` the content has nothing to send on. */
export var missingChannelContent = function (content, channels) { return channels.filter(function (channel) { return !hasContent(content, channel); }); };
/** Email and SMS, as before messaging: the backend's channels without a configuration. */
var UNCONFIGURED_CHANNELS = [EMessageChannel.SMS, EMessageChannel.EMAIL];
export var sendNoticeChannels = function (canRead, 
/** The stored configuration; `null` when the event has none. */
config) {
    if (!canRead)
        return { kind: "NO_PERMISSION" };
    if (!config)
        return { kind: "CHANNELS", channels: UNCONFIGURED_CHANNELS };
    var channels = enabledChannels(config, EMessagePurpose.NOTICE);
    return channels.length ? { kind: "CHANNELS", channels: channels } : { kind: "NONE" };
};
/**
 * Who sends a channel's notices, as the backend picks it (`account_for`): the
 * event's account, else the tenant's default account of the channel. Email
 * and SMS without an account go out through the deployment's own sender
 * (`"ENVIRONMENT"`); any other channel without one can't send (`null`).
 */
export var sendingAccountFor = function (config, accounts, channel) {
    var chosen = config === null || config === void 0 ? void 0 : config.channels.find(function (entry) { return entry.channel === channel; });
    var account = chosen
        ? accounts.find(function (candidate) { return candidate.id === chosen.account_id; })
        : accounts.find(function (candidate) { return candidate.channel === channel && candidate.is_default; });
    if (account)
        return account;
    return !chosen && UNCONFIGURED_CHANNELS.includes(channel) ? "ENVIRONMENT" : null;
};
